/**
 * Entry application component used to compose providers and render Routes.
 * */

import React from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { Routes } from '../app/Routes';
import { PageViewsTracker } from './hooks/usePageViews';
import { I18nProvider } from '../_metronic/i18n';
import { LayoutSplashScreen, MaterialThemeProvider } from '../_metronic/layout';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ThemeProvider } from 'styled-components';
import { theme } from './styles/theme';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/pt-br';
import { ptBR as datePickersPtBR } from '@mui/x-date-pickers/locales';
import './utils/yupLocale'; // mensagens padrão do Yup em pt-BR (efeito colateral)
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient, initReactQueryPersist } from './services/reactQuery';
import { HeaderNavigationProvider } from './contexts/HeaderNavigationContext';

initReactQueryPersist();

export default function App({ store, persistor, basename }) {
  return (
    /* Provide Redux store */
    <ThemeProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <ToastContainer />

          {/* Asynchronously persist redux stores and show `SplashScreen` while it's loading. */}
          <PersistGate persistor={persistor} loading={<LayoutSplashScreen />}>
            {/* Add high level `Suspense` in case if was not handled inside the React tree. */}
            <React.Suspense fallback={<LayoutSplashScreen />}>
              {/* Override `basename` (e.g: `homepage` in `package.json`) */}
              <BrowserRouter basename={basename}>
                {/*This library only returns the location that has been active before the recent location change in the current window lifetime.*/}
                <MaterialThemeProvider>
                  {/* Provide `react-intl` context synchronized with Redux state.  */}
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={'pt-br'}
                    localeText={
                      datePickersPtBR.components.MuiLocalizationProvider
                        .defaultProps.localeText
                    }
                  >
                    <I18nProvider>
                      <HeaderNavigationProvider>
                        <PageViewsTracker />
                        <Routes />
                      </HeaderNavigationProvider>
                    </I18nProvider>
                  </LocalizationProvider>
                </MaterialThemeProvider>
              </BrowserRouter>
            </React.Suspense>
          </PersistGate>
        </Provider>

        {process.env.NODE_ENV === 'development' && (
          <ReactQueryDevtools
            initialIsOpen={false}
            toggleButtonProps={{
              style: { bottom: '8px', left: '77px' },
            }}
          />
        )}
      </QueryClientProvider>
    </ThemeProvider>
  );
}
